import React, { useState, useEffect } from 'react';
import { MediaService } from '../../../Services/Axios/Api';
import { Row, Col, Button, Spinner, Card, Modal, Form, InputGroup, Pagination, Dropdown, Tabs, Tab } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faPlus, faLink, faFilter, faFolder, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import UploadMediaDialog from './UploadMediaDialog';
import UrlMediaDialog from './UrlMediaDialog';
import { createMediaGalleryFoldersInstance } from '../../../Models/MediaGalleryFolders';
import MediaFilePreview from './MediaFilePreview';
import { getMediaTypeFromPath, isPdfFile, isVideoFile } from './mediaFileUtils';
import './MediaSelector.css';
import './Media.css';

const MediaSelector = ({ isOpen, onClose, onSelect, isMultiple = false }) => {
  const [activeTab, setActiveTab] = useState('library');
  const [media, setMedia] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(-1);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [mediaTypeFilter, setMediaTypeFilter] = useState('all');

  const ITEMS_PER_PAGE = 12;

  useEffect(() => {
    if (isOpen) {
      if (activeTab === 'library') {
        fetchMedia();
      } else if (activeTab === 'folders') {
        fetchFolders();
      }
    }
  }, [isOpen, activeTab]);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const response = await MediaService.getMedia();
      setMedia(response.result.reverse() || []);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    try {
      setFoldersLoading(true);
      const foldersInstance = createMediaGalleryFoldersInstance();
      await foldersInstance.load();
      setFolders(foldersInstance.getFolders() || []);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching folders:', error);
      setFolders([]);
    } finally {
      setFoldersLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (value) => {
    if (!value) {
      return;
    }
    setMediaTypeFilter(value);
    setCurrentPage(1);
  };

  const handleItemClick = (item) => {
    if (isMultiple) {
      setSelectedItems(prev => {
        const exists = prev.some(selected => selected.id === item.id);
        if (exists) {
          return prev.filter(selected => selected.id !== item.id);
        } else {
          return [...prev, item];
        }
      });
    } else {
      setSelectedItems([item]);
    }
  };

  const handleFolderClick = (folder) => {
    // Open folder to show images
    if (selectedFolder && selectedFolder.id === folder.id) {
      // If clicking the same folder, go back to folder list
      setSelectedFolder(null);
      setSelectedItems([]);
    } else {
      setSelectedFolder(folder);
      setSelectedItems([]);
      setCurrentPage(1);
    }
  };

  const handleFolderSelect = (folder, event) => {
    event?.stopPropagation();
    
    if (!isMultiple) return; // Only allow folder selection in multiple mode
    
    // Get all images from the folder
    const folderImages = folder.folderImages || [];
    
    if (folderImages.length === 0) return; // No images to select
    
    // Check if all images from this folder are already selected
    const allSelected = folderImages.every(imageUrl => 
      selectedItems.some(selected => selected.filePath === imageUrl)
    );
    
    if (allSelected) {
      // Deselect all images from this folder
      setSelectedItems(prev => 
        prev.filter(selected => !folderImages.includes(selected.filePath))
      );
    } else {
      // Select all images from this folder
      const newItems = folderImages.map((imageUrl, idx) => ({
        id: `folder-image-${folder.id}-${imageUrl}`,
        filePath: imageUrl,
        altText: `${folder.folderName} - Image ${idx + 1}`
      }));
      
      // Remove any existing items from this folder, then add all new ones
      setSelectedItems(prev => {
        const filtered = prev.filter(selected => !folderImages.includes(selected.filePath));
        return [...filtered, ...newItems];
      });
    }
  };

  const handleFolderImageClick = (imageUrl) => {
    // Create a pseudo-item for folder images (they don't have IDs from media service)
    const imageItem = {
      id: `folder-image-${selectedFolder.id}-${imageUrl}`,
      filePath: imageUrl,
      altText: `${selectedFolder.folderName} - Image`
    };

    if (isMultiple) {
      setSelectedItems(prev => {
        const exists = prev.some(selected => selected.filePath === imageUrl);
        if (exists) {
          return prev.filter(selected => selected.filePath !== imageUrl);
        } else {
          return [...prev, imageItem];
        }
      });
    } else {
      setSelectedItems([imageItem]);
    }
  };

  const handleConfirm = () => {
    if (activeTab === 'folders' && selectedFolder && selectedItems.length === 0) {
      // If in folders tab and a folder is selected but no images, return folder main image or folder images
      if (selectedFolder.folderMainImage) {
        onSelect(isMultiple ? [selectedFolder.folderMainImage] : selectedFolder.folderMainImage);
      } else if (selectedFolder.folderImages && selectedFolder.folderImages.length > 0) {
        onSelect(isMultiple ? selectedFolder.folderImages : selectedFolder.folderImages[0]);
      }
    } else {
      const selectedPaths = selectedItems.map(item => item.filePath);
      onSelect(isMultiple ? selectedPaths : selectedPaths[0]);
    }
    handleClose();
  };

  const handleClose = () => {
    setSelectedItems([]);
    setPreviewIndex(-1);
    setSelectedFolder(null);
    setActiveTab('library');
    onClose();
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedItems([]);
    setSelectedFolder(null);
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handleUploadSuccess = () => {
    setUploadDialogOpen(false);
    fetchMedia();
  };

  const filteredMedia = media.filter((item) => {
    const altText = (item.altText || '').toLowerCase();
    const filePath = (item.filePath || '').toLowerCase();
    const query = searchQuery.toLowerCase();
    const matchesSearch = altText.includes(query) || filePath.includes(query);
    const mediaType = getMediaTypeFromPath(item.filePath || '');
    const matchesType =
      mediaTypeFilter === 'all' ||
      mediaTypeFilter === mediaType;
    return matchesSearch && matchesType;
  });

  const lightboxMedia = filteredMedia.filter((item) => !isPdfFile(item.filePath));

  const totalPages = Math.max(Math.ceil(filteredMedia.length / ITEMS_PER_PAGE), 1);
  const indexOfFirstItem = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentMedia = filteredMedia.slice(indexOfFirstItem, indexOfFirstItem + ITEMS_PER_PAGE);

  const slides = lightboxMedia.map(item => ({
    src: item.filePath,
    title: item.altText,
    description: item.altText,
    type: isVideoFile(item.filePath) ? 'video' : 'image',
  }));


  return (
    <>
      <Modal show={isOpen} onHide={handleClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Select Media</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs activeKey={activeTab} onSelect={handleTabChange} className="mb-3">
            <Tab eventKey="library" title="Media Library">
              <Row className="mb-3">
                <Col xs={12} md={6}>
                  <InputGroup>
                    <Form.Control
                      type="search"
                      placeholder="Search media..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                    <Dropdown align="end" onSelect={handleFilterChange}>
                      <Dropdown.Toggle variant="outline-secondary" id="media-selector-filter-toggle">
                        <FontAwesomeIcon icon={faFilter} className="me-2" />
                        {mediaTypeFilter === 'all' && 'All'}
                        {mediaTypeFilter === 'image' && 'Images'}
                        {mediaTypeFilter === 'video' && 'Videos'}
                        {mediaTypeFilter === 'pdf' && 'PDFs'}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item eventKey="all" active={mediaTypeFilter === 'all'}>
                          All
                        </Dropdown.Item>
                        <Dropdown.Item eventKey="image" active={mediaTypeFilter === 'image'}>
                          Images
                        </Dropdown.Item>
                        <Dropdown.Item eventKey="video" active={mediaTypeFilter === 'video'}>
                          Videos
                        </Dropdown.Item>
                        <Dropdown.Item eventKey="pdf" active={mediaTypeFilter === 'pdf'}>
                          PDFs
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </InputGroup>
                </Col>
                <Col xs={12} md={6} className="mt-2 mt-md-0 d-flex justify-content-md-end">
                  <div className="d-flex gap-2 w-100 justify-content-end flex-wrap">
                    <Button 
                      variant="primary" 
                      onClick={() => setUploadDialogOpen(true)}
                      className="d-flex align-items-center gap-2"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      Upload New
                    </Button>
                    <Button 
                      variant="success" 
                      onClick={() => setUrlDialogOpen(true)}
                      className="d-flex align-items-center gap-2"
                    >
                      <FontAwesomeIcon icon={faLink} />
                      URL
                    </Button>
                  </div>
                </Col>
              </Row>
              {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : (
                <>
                  <Row className="g-3">
                    {currentMedia.map((item) => (
                      <Col key={item.id} xs={6} sm={4} md={3}>
                        <Card 
                          className={`media-selector-card ${selectedItems.some(selected => selected.id === item.id) ? 'selected' : ''}`}
                          onClick={() => handleItemClick(item)}
                        >
                          <div className="media-selector-container">
                            <MediaFilePreview
                              filePath={item.filePath}
                              alt={item.altText}
                              imageClassName="media-selector-image"
                              videoProps={{ controls: true, preload: 'metadata' }}
                            />
                            <div className="media-selector-overlay">
                              <p className="mb-0 text-white">{item.altText}</p>
                            </div>
                            {selectedItems.some(selected => selected.id === item.id) && (
                              <div className="media-selector-indicator">
                                <FontAwesomeIcon icon={faCheck} />
                              </div>
                            )}
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                  {filteredMedia.length === 0 && (
                    <Row className="mt-4">
                      <Col>
                        <p className="text-center text-muted mb-0">No media found.</p>
                      </Col>
                    </Row>
                  )}
                  {filteredMedia.length > ITEMS_PER_PAGE && (
                    <Row className="mt-3">
                      <Col>
                        <Pagination className="justify-content-center mb-0">
                          <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                          <Pagination.Prev onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={currentPage === 1} />
                          {Array.from({ length: totalPages }, (_, pageIndex) => {
                            const pageNumber = pageIndex + 1;
                            return (
                              <Pagination.Item
                                key={pageNumber}
                                active={pageNumber === currentPage}
                                onClick={() => setCurrentPage(pageNumber)}
                              >
                                {pageNumber}
                              </Pagination.Item>
                            );
                          })}
                          <Pagination.Next
                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                          />
                          <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                        </Pagination>
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </Tab>
            <Tab eventKey="folders" title="Folders">
              {foldersLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : selectedFolder ? (
                <>
                  <div className="mb-3 d-flex align-items-center gap-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setSelectedFolder(null);
                        setSelectedItems([]);
                      }}
                    >
                      <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                      Back to Folders
                    </Button>
                    <h5 className="mb-0">{selectedFolder.folderName}</h5>
                  </div>
                  {selectedFolder.folderImages && selectedFolder.folderImages.length > 0 ? (
                    <>
                      <Row className="g-3">
                        {selectedFolder.folderImages.map((mediaUrl, idx) => {
                          const mediaItem = {
                            id: `folder-image-${selectedFolder.id}-${mediaUrl}`,
                            filePath: mediaUrl,
                            altText: `${selectedFolder.folderName} - Media ${idx + 1}`
                          };
                          const isSelected = selectedItems.some(selected => selected.filePath === mediaUrl);
                          return (
                            <Col key={`${selectedFolder.id}-${idx}`} xs={6} sm={4} md={3}>
                              <Card 
                                className={`media-selector-card ${isSelected ? 'selected' : ''}`}
                                onClick={() => handleFolderImageClick(mediaUrl)}
                              >
                                <div className="media-selector-container">
                                  <MediaFilePreview
                                    filePath={mediaUrl}
                                    alt={mediaItem.altText}
                                    imageClassName="media-selector-image"
                                    allowEmbedFallback
                                    videoProps={{ controls: true, preload: 'metadata' }}
                                  />
                                  <div className="media-selector-overlay">
                                    <p className="mb-0 text-white">{mediaItem.altText}</p>
                                  </div>
                                  {isSelected && (
                                    <div className="media-selector-indicator">
                                      <FontAwesomeIcon icon={faCheck} />
                                    </div>
                                  )}
                                </div>
                              </Card>
                            </Col>
                          );
                        })}
                      </Row>
                    </>
                  ) : (
                    <Row className="mt-4">
                      <Col>
                        <p className="text-center text-muted mb-0">This folder has no media.</p>
                      </Col>
                    </Row>
                  )}
                </>
              ) : (
                <>
                  {folders.length > 0 ? (
                    <Row className="g-3">
                      {folders.map((folder) => {
                        // Check if all images from this folder are selected
                        const folderImages = folder.folderImages || [];
                        const allImagesSelected = folderImages.length > 0 && folderImages.every(imageUrl => 
                          selectedItems.some(selected => selected.filePath === imageUrl)
                        );
                        
                        return (
                          <Col key={folder.id} xs={6} sm={4} md={3}>
                            <Card 
                              className={`media-selector-card ${allImagesSelected ? 'selected' : ''}`}
                              style={{ cursor: 'pointer', position: 'relative' }}
                            >
                              <div className="media-selector-container" onClick={() => handleFolderClick(folder)}>
                                {folder.folderMainImage ? (
                                  <MediaFilePreview
                                    filePath={folder.folderMainImage}
                                    alt={folder.folderName}
                                    imageClassName="media-selector-image"
                                    allowEmbedFallback
                                    videoProps={{ controls: false, muted: true }}
                                    style={{ height: '150px', objectFit: 'cover' }}
                                  />
                                ) : (
                                  <div 
                                    style={{ 
                                      height: '150px', 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      justifyContent: 'center',
                                      backgroundColor: '#f8f9fa',
                                      color: '#6c757d'
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faFolder} size="3x" />
                                  </div>
                                )}
                                <div className="media-selector-overlay">
                                  <p className="mb-0 text-white">{folder.folderName}</p>
                                </div>
                                {allImagesSelected && (
                                  <div className="media-selector-indicator">
                                    <FontAwesomeIcon icon={faCheck} />
                                  </div>
                                )}
                              </div>
                              {isMultiple && folderImages.length > 0 && (
                                <div 
                                  style={{ 
                                    position: 'absolute', 
                                    top: '5px', 
                                    right: '5px',
                                    zIndex: 10
                                  }}
                                  onClick={(e) => handleFolderSelect(folder, e)}
                                >
                                  <Button
                                    variant={allImagesSelected ? "success" : "outline-secondary"}
                                    size="sm"
                                    style={{ padding: '2px 8px' }}
                                  >
                                    <FontAwesomeIcon icon={faCheck} size="xs" />
                                  </Button>
                                </div>
                              )}
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  ) : (
                    <Row className="mt-4">
                      <Col>
                        <p className="text-center text-muted mb-0">No folders found.</p>
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={selectedItems.length === 0}>
            Select
          </Button>
        </Modal.Footer>
      </Modal>

      <Lightbox
        slides={slides}
        open={previewIndex >= 0}
        index={previewIndex}
        close={() => setPreviewIndex(-1)}
        plugins={[Thumbnails, Zoom]}
        render={{
          slide: ({ slide }) =>
            slide.type === 'video' ? (
              <video controls preload="metadata" src={slide.src} style={{ width: '100%', height: 'auto' }} />
            ) : (
              <img src={slide.src} alt={slide.title} style={{ width: '100%', height: 'auto' }} loading="lazy" />
            ),
        }}
        thumbnails={{
          position: 'bottom',
          width: 120,
          height: 80,
          border: 0,
          borderRadius: 4,
          padding: 4,
          gap: 16,
          showToggle: true
        }}
        styles={{ 
          container: { backgroundColor: "rgba(0, 0, 0, .9)" },
          root: { "--yarl__color_backdrop": "rgba(0, 0, 0, .9)" }
        }}
      />

      <UploadMediaDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onSuccess={handleUploadSuccess}
      />

      <UrlMediaDialog
        open={urlDialogOpen}
        onClose={() => setUrlDialogOpen(false)}
        isMultiple={isMultiple}
        onConfirm={(urls) => {
          // Convert URLs into the same shape as media items or directly return
          if (!Array.isArray(urls)) return;
          if (isMultiple) {
            onSelect(urls);
          } else {
            onSelect(urls[0]);
          }
          handleClose();
          setUrlDialogOpen(false);
        }}
      />
    </>
  );
};

export default MediaSelector; 